import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import MainLayout from "../../../layout/MainLayout";
import { Paper, Box, Typography, Button } from "@mui/material";
import arrowBack from "../../../assets/arrowBack.svg";
import Input from "@mui/joy/Input";
import Select from "react-select";
import Textarea from "@mui/joy/Textarea";
import ButtonComponent from "../../../component/Button";
import { Link, useNavigate } from "react-router-dom";
import { getAPIHandler, postAPIHandler } from "../../../config/service";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { broadcastSchema } from "../../../schemas";
import toast from "react-hot-toast";

const AddBroadcast = () => {
  const navigate = useNavigate();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userType, setUserType] = useState(""); // Track selected user type
  const [userOptions, setUserOptions] = useState([]);
  const [broadcastImage, setBroadcastImage] = useState();
  const [loading, setLoading] = useState(false);

  const userTypeOptions = [
    { value: "VP", label: "Vice President" },
    { value: "MANAGER", label: "Manager" },
    { value: "EXECUTIVE", label: "Executive" },
  ];

  const handleBroadcastImageChange = async (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        try {
          setLoading(true);
          const formData = new FormData();
          formData.append("file", file);

          const res = await postAPIHandler({
            endPoint: "uploadFile",
            dataToSend: formData,
          });

          setBroadcastImage(res.data.result);
          toast.success("Image uploaded successfully!");
        } catch (error) {
          console.error("Error uploading file:", error);
          toast.error("Failed to upload image.");
        } finally {
          setLoading(false);
        }
      } else {
        toast.error("Please select a valid image file.");
      }
    }
  };

  const handleUserOptions = async (selectedUserType) => {
    setUserType(selectedUserType);
    try {
      const response = await getAPIHandler({
        endPoint: "listUserDetailsByType",
        paramsData: { userType: selectedUserType },
      });

      const mapUsers = (users) =>
        users.map((user) => ({
          value: user._id,
          label: `${user.firstName} ${user.lastName}`,
        }));

      setUserOptions(mapUsers(response.data.result.userDetails));
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error fetching user options:", error);
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const usersToSend = selectedUsers.length > 0 
    ? selectedUsers.map((user) => user.value)  // Send selected users
    : []; 
    try {
      const response = await postAPIHandler({
        endPoint: "addBroadcast",
        dataToSend: {
          title: values.title,
          broadcastMessage: values.broadcastMessage,
          broadcastImage: broadcastImage,
          userType: values.userType,
          selectedUserId: usersToSend,
        },
      });
      console.log(response.data);
      if (response && response.data.responseCode === 200) {
        toast.success("Broadcast added successfully.");
        navigate("/broadcastmanagement");
      } else {
        toast.error("Error in adding broadcast.");
      }
    } catch (error) {
      console.error("Error:", error);
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" padding="1rem">
          <Typography variant="h4" fontWeight={700}>
            Broadcast Management
          </Typography>
        </Box>

        <Box sx={{ marginLeft: "15px", marginBottom: "15px" }}>
          <Paper
            elevation={0}
            sx={{ padding: "15px", overflowY: "auto", scrollbarWidth: "none" }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Link to="/broadcastmanagement">
                <img
                  src={arrowBack}
                  alt="arrowBack"
                  style={{
                    width: "2.813rem",
                    height: "2.813rem",
                    cursor: "pointer",
                  }}
                />
              </Link>
              <Typography
                variant="h5"
                sx={{ marginLeft: "1rem", fontWeight: 800 }}
              >
                Add Broadcast
              </Typography>
            </Box>

            <Formik
              initialValues={{
                title: "",
                broadcastMessage: "",
                broacastImage: "",
                userType: "",
              }}
              validationSchema={broadcastSchema}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, handleBlur, setFieldValue, values }) => (
                <Form>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                  >
                    <Box mb={2} width="100%" maxWidth="500px">
                      <Field
                        as={Input}
                        type="text"
                        name="title"
                        placeholder="Enter Title"
                        fullWidth
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="error"
                      />
                    </Box>

                    <Box mb={2} width="100%" maxWidth="500px">
                      <Field
                        as={Textarea}
                        minRows={4}
                        name="broadcastMessage"
                        placeholder="Enter Broadcast Message"
                        fullWidth
                      />
                      <ErrorMessage
                        name="broadcastMessage"
                        component="div"
                        className="error"
                      />
                    </Box>

                    <Box mb={2} width="100%" maxWidth="500px">
                      <input
                        id="broadcastImage"
                        type="file"
                        accept="image/*"
                        onChange={handleBroadcastImageChange}
                        disabled={loading}
                        style={{ display: "none" }}
                      />
                      <label htmlFor="broadcastImage">
                        <Button
                          variant="outlined"
                          component="span"
                          fullWidth
                          sx={{
                            width: "100%",
                            padding: "8px 20px",
                            borderColor: "#ccc",
                            background: "#fff",
                            textTransform: "none",
                            color: "#333",
                            "&:hover": {
                              backgroundColor: "#f4f4f4",
                              borderColor: "#bbb",
                            },
                          }}
                          startIcon={<PhotoCameraIcon />}
                        >
                          Broadcast Image
                        </Button>
                      </label>
                      {broadcastImage && (
                        <Box mt={2}>
                          <img
                            src={broadcastImage}
                            alt="Broadcast Image Preview"
                            style={{
                              width: "100%",
                              maxWidth: "200px",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box mb={2} width="100%" maxWidth="500px">
                      <Select
                        name="userType"
                        options={userTypeOptions}
                        value={userTypeOptions.find(
                          (option) => option.value === values.userType
                        )}
                        onChange={(selectedOption) => {
                          setFieldValue(
                            "userType",
                            selectedOption ? selectedOption.value : ""
                          );
                          handleUserOptions(
                            selectedOption ? selectedOption.value : ""
                          );
                        }}
                        onBlur={handleBlur}
                        isClearable
                        placeholder="Select User Type"
                        fullWidth
                      />
                      <ErrorMessage
                        name="userType"
                        component="div"
                        className="error"
                      />
                    </Box>

                    {userType && userOptions.length > 0 && (
                      <Box mb={2} width="100%" maxWidth="500px">
                        <Select
                          isMulti
                          name="selectedUsers"
                          options={
                            userOptions.sort((a, b) =>
                              a.label.localeCompare(b.label)
                            ) // Sort alphabetically
                          }
                          value={selectedUsers}
                          onChange={setSelectedUsers}
                          placeholder="Select Users"
                          closeMenuOnSelect={false}
                          styles={{
                            menu: (provided) => ({
                              ...provided,
                            }),
                          }}
                          menuPortalTarget={document.body} // Render dropdown outside the regular DOM hierarchy
                          menuPlacement="auto" // Automatically place the menu
                          isSearchable // Enable search functionality
                          fullWidth
                        />
                      </Box>
                    )}

                    <Box
                      mb={2}
                      width="100%"
                      display="flex"
                      justifyContent="center"
                    >
                      <ButtonComponent
                        type="submit"
                        name="Add Broadcast"
                        disabled={isSubmitting}
                        height="43px"
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default AddBroadcast;
